import { Show, For, useContext, createSignal, createEffect, on } from 'solid-js';
import { Heading } from '../../ui-components/heading/heading';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Text } from '../../ui-components/text/text';
import { CourseCard, CourseCardData } from '../course-card/course-card';
import { Button } from '../../ui-components/button/button';
import { 
    StyledAccountCertificatesGrid,
    StyledAccountCoursesInProgressGrid,
    StyledAccountEducationContent,
    StyledSectionContainer,
    StyledLinkToAllCourses,
} from './account-education-content.styles';
import { CertificateCard } from '../certificate-card/certificate-card';
import { ShowpadCodeSubmission } from '../showpad-code-submission/showpad-code-submission';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { GET_CERTIFICATES_QUERY, GET_PROGRESSING_COURSES_QUERY } from './queries';
import { useNavigate } from '@solidjs/router';
import { ShowpadDelayInfoBox } from '../showpad-delay-info-box/showpad-delay-info-box';


type AccountEducationContentProps = {
    lastElement: (el: any) => void;
};

export const AccountEducationContent = (props: AccountEducationContentProps) => {
    const navigate = useNavigate();

    const {
        localize,
        createCachedResource,
        userState,
        appStore,
        coursesListingPage,
    } = useContext(AppContext);

    const [ certificatesData, { refetch: refetchCertificates} ] = createCachedResource(GET_CERTIFICATES_QUERY, {}, false);

    // This we can maaaybe client-cache, because it's not changing often. To be perfectly correct, we should trigger a 
    // refetch when the user starts a new course, but that would require more complex logic.
    const [ coursesData, { refetch: refetchCourseData} ] = createCachedResource(GET_PROGRESSING_COURSES_QUERY, {}, true);

    const [ progressedCourses, setProgressedCourses ] = createSignal<CourseCardData[]>([]);
    const [ certificates, setCertificates ] = createSignal<CourseCardData[]>([]);
    const [ loadingProgress, setLoadingProgress ] = createSignal(true);
    const [ loadingCertificates, setLoadingCertificates ] = createSignal(true);
    const [ certificateDownloadInProgress, setCertificateDownloadInProgress ] = createSignal(false);

    createEffect(() => {
        console.log('certificates', certificates);
    });

    createEffect(() => {
        if (coursesData() && coursesData().getInProgressCourses) {
            const inProgress = coursesData().getInProgressCourses;

            setProgressedCourses(inProgress);
            setLoadingProgress(false);
        }
    });

    createEffect(() => {        
        if (certificatesData() && certificatesData().getCertificatesByUser) {
            const certs = certificatesData().getCertificatesByUser;

            const sortedByCompletionDate = certs.sort((a: CourseCardData, b: CourseCardData) => {
                return new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime();
            });

            setCertificates(sortedByCompletionDate);
            setLoadingCertificates(false);
        }
    });

    createEffect(
        on(
            () => userState.user?.showpadCode,
            () => {
                // When code changes, we need to refetch the data
                refetchCertificates();
                refetchCourseData();
            },
            { defer: true } // only refetch when showpadCode changes, not initially
        )
    );

    const Certificates = () => (
        <StyledSectionContainer>
            <Heading tag="h3" variant="medium">
                {localize('certificates', 'Certificates')}
            </Heading>

            <Show when={!loadingCertificates()} fallback={<LoadingText />}>
                <Show when={!certificates() || certificates()?.length === 0}>
                    <div>
                        <Text fontSize='normal'>
                            {localize('you-do-not-have-certificates', 'You do not have any certificates yet.')}
                        </Text>
                    </div>
                </Show>

                <Show when={certificates()?.length > 0}>
                    <StyledAccountCertificatesGrid>
                        <For each={certificates()}>{(data: CourseCardData) => (
                            <CertificateCard
                                data={data}
                                downloadInProgress={certificateDownloadInProgress()}
                                setDownloadInProgress={(value) => setCertificateDownloadInProgress(value)}
                            />
                        )}</For>
                    </StyledAccountCertificatesGrid>
                </Show>
            </Show>

        </StyledSectionContainer>
    );

    const InProgressCourses = () => (
        <StyledSectionContainer>
            <Heading tag="h3" variant="medium">
                {localize('in-progress', 'In progress')}
            </Heading>

            <Show when={!loadingProgress()} fallback={<LoadingText />}>
                <Show when={!progressedCourses() || progressedCourses()?.length === 0}>
                    <div>
                        <Text fontSize='normal'>
                            {localize('you-have-no-in-progress-courses', 'You have no in-progress courses.')}
                        </Text>
                    </div>
                </Show>

                <Show when={progressedCourses()?.length > 0}>
                    <StyledAccountCoursesInProgressGrid>
                        <For each={progressedCourses()}>{(course) => (
                            <CourseCard course={course} />
                        )}</For>
                    </StyledAccountCoursesInProgressGrid>
                </Show>
            </Show>

        </StyledSectionContainer>
    );

    const LinkToAllCourses = () => (
        <StyledLinkToAllCourses>
            <Heading tag="h3" variant="large">
                {localize('explore-more-courses', 'Explore more courses')}
            </Heading>
            <Button
                label={localize('view-all-courses', 'View all courses')}
                variant='arrow'
                onClick={() => {
                    navigate(coursesListingPage.url);
                    appStore.closeUserProfileMenu();
                }}
                element={(el: HTMLElement) => props.lastElement(el)}
            />
        </StyledLinkToAllCourses>
    );

    const LoadingText = () => (
        <StyledSectionContainer>
            <Text fontSize='normal'>
                {localize('loading', 'Loading...')}
            </Text>
        </StyledSectionContainer>
    );

    return (
        <StyledAccountEducationContent>
            <Heading tag='h2'>
                {localize('e-learning', 'E-learning')}
            </Heading>

            <StyledVerticalSpace size={1} />

            <ShowpadCodeSubmission />

            <ShowpadDelayInfoBox text={localize('showpad-progress-delay-info-text', 'Please note that it may take up to an hour for your progress to appear')}/>

            <Certificates />

            <InProgressCourses />

            <LinkToAllCourses />
        </StyledAccountEducationContent>
    );
};

